'use client';

import { AppleIcon } from '@curology/ui-components-web/react/Icon/icons/AppleIcon';
import CheckmarkIcon from '@curology/ui-components-web/react/Icon/icons/CheckmarkIcon';
import { PaymentIcon } from '@curology/ui-components-web/react/Icon/icons/PaymentIcon';
import { PayPalIcon } from '@curology/ui-components-web/react/Icon/icons/PayPalIcon';
import { List } from '@curology/ui-components-web/react/List';
import { ListItem } from '@curology/ui-components-web/react/List/ListItem';
import { useAtomValue } from 'jotai';
import React, { useState } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useSelectedItemIds } from '@/components/product-selection/hooks/useSelectedItemIds';

import ApplePayButton, { canUseApplePayAtom } from './applePayButton';
import CheckOutNowButton from './checkOutNowButton';
import PayPalButton from './payPalButton';
import { PAYMENT_METHODS } from '../../../consts/paymentMethods';

export const ExpressCheckout = ({
  showFlashMessageOnCheckoutSuccess,
  navigateToCheckout,
}: {
  showFlashMessageOnCheckoutSuccess?: boolean;
  navigateToCheckout: () => void;
}) => {
  const { brand } = useFlowContext();
  const canUseApplePay = useAtomValue(canUseApplePayAtom);
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS.cc);
  const { selectedRxCards } = useSelectedItemIds();

  const paymentMethodListItems = [
    {
      icon: PaymentIcon,
      id: PAYMENT_METHODS.cc,
      show: true,
      title: 'Credit / debit card',
    },
    {
      icon: AppleIcon,
      id: PAYMENT_METHODS.applePay,
      show: canUseApplePay,
      title: 'Apple Pay',
    },
    {
      icon: PayPalIcon,
      id: PAYMENT_METHODS.payPal,
      show: true,
      title: 'PayPal',
    },
  ] as const;

  const paymentMethodSelectedClasses =
    'cur-bg-surface-highlight cur-border-brand';

  const paymentMethodNotSelectedClasses =
    brand === 'agency'
      ? 'cur-bg-surface-primary cur-border-2 cur-border-surface-disabled'
      : 'cur-bg-surface-neutral cur-border-transparent';

  return (
    <>
      <List size="lg" className="cur-m-0">
        {paymentMethodListItems.map((paymentMethodListItem) => {
          if (!paymentMethodListItem.show) {
            return null;
          }

          return (
            <ListItem
              className={`cur-border-4 cur-cursor-pointer cur-p-4 ${
                paymentMethod === paymentMethodListItem.id
                  ? paymentMethodSelectedClasses
                  : paymentMethodNotSelectedClasses
              }`}
              data-sentry-id="Select payment method"
              iconEnd={
                paymentMethod === paymentMethodListItem.id
                  ? CheckmarkIcon
                  : undefined
              }
              iconStart={paymentMethodListItem.icon}
              key={paymentMethodListItem.id}
              onClick={() => setPaymentMethod(paymentMethodListItem.id)}
              onKeyPress={(event) => {
                if (event.key === 'Enter') {
                  setPaymentMethod(paymentMethodListItem.id);
                }
              }}
              tabIndex={0}
              title={paymentMethodListItem.title}
            />
          );
        })}
      </List>
      {paymentMethod === PAYMENT_METHODS.cc && (
        <CheckOutNowButton navigateToCheckout={navigateToCheckout} />
      )}
      {/* Apple Pay needs to be rendered on page-load and just hidden from view when not selected. There are likely ways to impove this in the future when we either upgrade our Apple Pay integration or move away from using Stripe for Apple Pay entirely. */}
      <div
        className={
          paymentMethod === PAYMENT_METHODS.applePay ? '' : 'cur-hidden'
        }
      >
        <ApplePayButton
          showFlashMessageOnCheckoutSuccess={showFlashMessageOnCheckoutSuccess}
        />
      </div>
      {paymentMethod === PAYMENT_METHODS.payPal &&
        (selectedRxCards.length === 0 ? (
          <CheckOutNowButton navigateToCheckout={navigateToCheckout} />
        ) : (
          <div>
            <PayPalButton
              showFlashMessageOnCheckoutSuccess={
                showFlashMessageOnCheckoutSuccess
              }
            />
          </div>
        ))}
    </>
  );
};
